import React, { useState, useMemo, useEffect, useCallback } from "react";

import { IconButton, FormGroup, Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { Add, Archive, Delete, LocalShipping } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useAppState } from "../../../context/StateContext";
import { Access, Options } from "../../../models/access";
import { Driveway } from "../../../models/driveway";
import { Instruction } from "../../../models/instruction";
import { Residence } from "../../../models/residence";
import { StreetAccess } from "../../../models/street.access";
import { Placement } from "../../../util/enums";
import { capitalizeFirstCharacter } from "../../../util/Format";
import Button, { Color } from "../../Form/Button";
import Checkbox from "../../Form/Checkbox";
import Input from "../../Form/Input";
import Label from "../../Form/Label";
import RadioGroup from "../../Form/RadioGroup";
import Select from "../../Form/Select";
import AddSpecialInstructionModal from "./AddSpecialInstructionModal";
import { GenericForm } from "./genericForm";
import useStyles, { CheckBoxAccess } from "./styles";

type TOptions = {
  drivewayLength: Options[];
  parkings: Options[];
  carryDistance: Options[];
  accessActions: Options[];
  drivewayList: Options[];
};

interface Props {
  data: Access;
  typeAccess?: string;
  selectOptions: TOptions;
  update: (value: Access) => void;
  errors?: yup.ValidationError[];
  described: string;
  viewMode?: boolean;
}

interface InstructionsListProps {
  data: Instruction[];
  label: string;
  icon: React.ReactNode;
  isOnViewMode?: boolean;
  updateList?: (data: Instruction[]) => void;
}

export const InstructionsList = ({
  data,
  label,
  icon,
  isOnViewMode,
  updateList,
}: InstructionsListProps) => {
  const classes = useStyles();

  return (
    <div className="mb-2">
      <label className={`d-flex align-items-center mb-0 ${classes.instructionListLabel}`}>
        {icon}
        <span className="ml-1">{label}</span>
      </label>
      <ul data-cy={`instructionList${label}`} className={classes.instructionList}>
        {data.map((instruction, index: number) => (
          <li key={`${label}${index}`}>
            <Grid container direction="row" alignItems="center">
              <Grid item sm md={6} lg={6}>
                <div className="d-flex align-items-center">
                  <p className="mb-0 mr-2">{instruction?.remarks}</p>
                  <span>
                    {(instruction.createdAt &&
                      new Date(instruction.createdAt).toLocaleDateString("pt-BR")) ||
                      new Date().toLocaleDateString("pt-BR")}{" "}
                    {instruction.createdById
                      ? `by ${instruction?.createdBy?.name} ${instruction?.createdBy?.lastName}`
                      : ""}
                  </span>
                </div>
              </Grid>
              {!isOnViewMode && (
                <Grid item sm md={2} lg={2}>
                  <IconButton
                    data-cy={`buttonDelete${index}`}
                    className={`py-1 px-0 ml-2 ${classes.iconButton}`}
                    onClick={() => {
                      const instructionData = data;

                      instructionData.splice(index, 1);
                      if (updateList) updateList(instructionData);
                    }}
                    size="large"
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </li>
        ))}
      </ul>
    </div>
  );
};

const AccessInfoForm = ({ data, selectOptions, update, errors, described, viewMode }: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { currentUser } = useAppState();

  const [isAddInstruction, setIsAddInstruction] = useState<boolean>(false);
  const [addInstructionEnabled, setAddInstructionEnabled] = useState<boolean>(true);
  const [accessInfo, setAccessInfo] = useState<Access>({} as Access);
  const [streetNames, setStreetNames] = useState<string[]>([]);
  const [commentsField, setCommentsField] = useState<string>("");
  const fieldHasError = (field: string) => !!errors?.find((error: any) => error.path === field);

  const drivewayLengthOptions = useMemo(
    () =>
      selectOptions.drivewayLength && selectOptions.drivewayLength.length > 0
        ? selectOptions.drivewayLength.map((option: Options) => ({
            value: option.value,
            label: option.name,
          }))
        : [],
    [selectOptions?.drivewayLength]
  );

  const parkingOptions = useMemo(
    () =>
      selectOptions.parkings && selectOptions.parkings.length > 0
        ? selectOptions.parkings.map((option) => ({
            value: option.value,
            label: option.name,
          }))
        : [],
    [selectOptions?.parkings]
  );

  const carryOptions = useMemo(
    () =>
      selectOptions.carryDistance && selectOptions.carryDistance.length > 0
        ? selectOptions.carryDistance.map((option) => ({
            value: option.value,
            label: option.name,
          }))
        : [],
    [selectOptions?.carryDistance]
  );

  const actionOptions = useMemo(
    () =>
      selectOptions.accessActions && selectOptions.accessActions.length > 0
        ? selectOptions.accessActions.map((option) => ({
            value: option.value,
            label: option.name,
          }))
        : [],
    [selectOptions?.accessActions]
  );

  const drivewayList = useMemo(
    () =>
      selectOptions.drivewayList && selectOptions.drivewayList.length > 0
        ? selectOptions.drivewayList.map((option) => ({
            value: option.value,
            label: option.name,
          }))
        : [],
    [selectOptions?.drivewayList]
  );

  const handleUpdateAccessInfoAndData = (newAccessInfo: Access) => {
    update(newAccessInfo);
  };

  useEffect(() => {
    setAccessInfo(data);
    setCommentsField(data?.comments !== null && data?.comments !== undefined ? data?.comments : "");
    if (Object.keys(data).length === 0 || !data?.streetsAccesses) setStreetNames([]);
    else setStreetNames(data?.streetsAccesses?.map((street) => street.name));
  }, [data]);

  const getValue = useCallback((value?: any, forceString = false, firstCharToUpperCase = false) => {
    if (value !== null && value !== undefined) {
      if (firstCharToUpperCase && typeof value === "string")
        value = capitalizeFirstCharacter(value);

      if (forceString) return `${value}`;

      return value;
    }

    return "";
  }, []);

  const handleChangeAccess = useCallback(
    (attribute: keyof Access, value: any) => {
      if (accessInfo) {
        handleUpdateAccessInfoAndData({
          ...accessInfo,
          [attribute]: value,
        });
      }
    },
    [accessInfo]
  );

  const handleChangeDriveway = useCallback(
    (attribute: keyof Driveway, value: string) => {
      if (accessInfo) {
        const driveway = {
          name: attribute === "name" ? value : accessInfo?.driveway?.name,
          length: attribute === "length" ? value : accessInfo?.driveway?.length,
        } as Driveway;

        handleChangeAccess("driveway", driveway);
      }
    },
    [accessInfo, handleChangeAccess]
  );

  const handleChangeResidence = useCallback(
    (attribute: keyof Residence, value: string) => {
      if (accessInfo) {
        const residence = {
          type: attribute === "type" ? value : accessInfo?.residence?.type,
          level:
            // eslint-disable-next-line no-nested-ternary
            attribute === "level"
              ? value || accessInfo?.residence?.level
              : value === "apartment"
              ? "0"
              : "",
        } as Residence;

        handleChangeAccess("residence", residence);
      }
    },
    [accessInfo, handleChangeAccess]
  );

  const handleChangeStepType = useCallback(
    (value: "Internal" | "External") => {
      if (accessInfo) {
        const reverse = value === "Internal" ? "External" : "Internal";

        if (!accessInfo?.stepType) {
          handleChangeAccess("stepType", value);
        } else if (accessInfo?.stepType === value) {
          handleChangeAccess("stepType", "");
        } else if (accessInfo?.stepType === "Both") {
          handleChangeAccess("stepType", reverse);
        } else {
          handleChangeAccess("stepType", "Both");
        }
      }
    },
    [accessInfo, handleChangeAccess]
  );

  const handleChangeStreetAccess = useCallback(
    (streetName: string) => {
      let streetAccesses = accessInfo?.streetsAccesses || [];

      const find = streetAccesses?.find((s) => s.name.toLowerCase() === streetName.toLowerCase());

      if (find) {
        streetAccesses = streetAccesses?.filter(
          (s) => s.name.toLowerCase() !== streetName.toLowerCase()
        );
        setStreetNames(streetAccesses.map((sa) => sa.name));
        handleChangeAccess("streetsAccesses", streetAccesses);
      } else {
        streetAccesses?.push({ name: streetName } as StreetAccess);
        setStreetNames(streetAccesses.map((sa) => sa.name));
        handleChangeAccess("streetsAccesses", streetAccesses);
      }
    },
    [accessInfo, handleChangeAccess]
  );

  useEffect(() => {
    if (!addInstructionEnabled) {
      setTimeout(() => setAddInstructionEnabled(true), 50);
    }
  }, [addInstructionEnabled]);

  return (
    <>
      <section className="mt-1 px-2 mb-4">
        {/* Entry house obstructed */}
        <GenericForm
          label={translate("willTheEntryOfTheHouseBeObstructed")}
          leftInputs={
            <RadioGroup
              aria-label="obstructured"
              name="row-radio-buttons-group"
              disabled={viewMode}
              data-cy="entryHouseObstructedRadios"
              value={getValue(accessInfo?.entryHouseObstructed, true)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeAccess("entryHouseObstructed", event.target.value === "true")
              }
              options={[
                {
                  label: translate("yes"),
                  value: "true",
                  "data-cy": "obstructedButtonYes",
                },
                { label: translate("no"), value: "false" },
              ]}
            />
          }
        />

        {/* Driveway */}
        <GenericForm
          label={translate("driveway")}
          classes="mt-1"
          leftInputs={
            <RadioGroup
              row
              aria-label="driveway"
              name="row-radio-buttons-group"
              disabled={viewMode}
              value={getValue(accessInfo?.driveway?.name, false, true)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeDriveway("name", event.target.value)
              }
              style={{ minWidth: "fit-content" }}
              options={[...drivewayList]}
            />
          }
          rightInputs={
            <>
              <Label className="mx-3">{translate("whatIsTheLengthM")}</Label>
              <Select
                name="drivewayLength"
                disabled={viewMode}
                value={getValue(accessInfo?.driveway?.length)}
                onChange={(event: SelectChangeEvent<any>) =>
                  handleChangeDriveway("length", event.target.value)
                }
                displayEmpty
                placeholder={translate("lengthM")}
              >
                {drivewayLengthOptions.map((option) => (
                  <MenuItem value={option.value} key={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </>
          }
        />

        {/* Residence type */}
        <GenericForm
          label={translate("residenceType")}
          classes="mt-1"
          leftInputs={
            <RadioGroup
              row
              aria-label="residenceType"
              name="row-radio-buttons-group"
              disabled={viewMode}
              value={getValue(accessInfo?.residence?.type)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeResidence("type", event.target.value)
              }
              data-cy="residenceTypeRadio"
              options={[
                {
                  label: translate("house"),
                  value: "house",
                  "data-cy": "residenceTypeRadioHouse",
                },
                {
                  label: translate("apartment"),
                  value: "apartment",
                  "data-cy": "residenceTypeRadioApartment",
                },
              ]}
            />
          }
          rightInputs={
            accessInfo?.residence?.type === "apartment" && (
              <>
                <Label className="mx-3">{translate("howManyLevels")}</Label>
                <Input
                  data-cy="levelApartment"
                  name="floors"
                  placeholder="Floors"
                  type="number"
                  disabled={viewMode}
                  min={0}
                  value={getValue(accessInfo?.residence?.level)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeResidence("level", event.target.value)
                  }
                />
              </>
            )
          }
        />

        {/* Residence house type */}
        {accessInfo?.residence?.type === "house" && (
          <RadioGroup
            row
            aria-label="residenceTypeHouse"
            name="row-radio-buttons-group"
            disabled={viewMode}
            value={getValue(accessInfo?.residence?.level)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleChangeResidence("level", event.target.value)
            }
            data-cy="levelHouse"
            options={[
              { label: translate("groundFloor"), value: "Ground" },
              { label: translate("split"), value: "Split" },
              { label: translate("multiStorey"), value: "Multi Storey" },
            ]}
          />
        )}

        {/* Steps */}
        <GenericForm
          label={translate("steps")}
          classes="mt-1"
          leftInputs={
            <div className="d-flex align-items-center">
              <RadioGroup
                row
                aria-label="steps"
                name="row-radio-buttons-group"
                disabled={viewMode}
                value={getValue(accessInfo?.steps)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  accessInfo &&
                  handleUpdateAccessInfoAndData({
                    ...accessInfo,
                    steps: event.target.value,
                    stepType: "",
                    stepsExternal: "",
                    stepsInternal: "",
                  })
                }
                options={[
                  {
                    label: translate("yes"),
                    value: "Yes",
                    "data-cy": "stepRadioYes",
                  },
                  { label: translate("no"), value: "No" },
                ]}
              />
              <Checkbox
                label={translate("lift")}
                checked={accessInfo?.lift || false}
                disabled={viewMode}
                onChange={(_, checked) =>
                  accessInfo &&
                  handleUpdateAccessInfoAndData({
                    ...accessInfo,
                    lift: checked,
                  })
                }
              />
            </div>
          }
        />
        {accessInfo?.steps && accessInfo?.steps !== "No" && (
          <div data-cy="stepsYesInputSection">
            <div className="d-flex align-items-center">
              <Checkbox
                label={translate("internal")}
                data-cy="stepCheckboxInternal"
                checked={accessInfo?.stepType === "Internal" || accessInfo?.stepType === "Both"}
                onChange={() => handleChangeStepType("Internal")}
                disabled={viewMode}
              />
              {(accessInfo?.stepType === "Internal" || accessInfo?.stepType === "Both") && (
                <Input
                  className="ml-2"
                  data-cy="stepInputInternal"
                  placeholder="How many"
                  name="internal"
                  disabled={viewMode}
                  type="number"
                  min={0}
                  value={getValue(accessInfo?.stepsInternal)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeAccess("stepsInternal", event.target.value)
                  }
                />
              )}
            </div>
            <div className="d-flex align-items-center mt-1">
              <Checkbox
                label={translate("external")}
                checked={accessInfo?.stepType === "External" || accessInfo?.stepType === "Both"}
                onChange={() => handleChangeStepType("External")}
                disabled={viewMode}
              />
              {(accessInfo?.stepType === "External" || accessInfo?.stepType === "Both") && (
                <Input
                  className="ml-2"
                  name="external"
                  disabled={viewMode}
                  placeholder="How many"
                  type="number"
                  min={0}
                  value={getValue(accessInfo?.stepsExternal)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeAccess("stepsExternal", event.target.value)
                  }
                />
              )}
            </div>
          </div>
        )}

        {/* Street accesses */}
        <div className="mt-1">
          <label className={`mb-0 ${classes.instructionListLabel}`}>
            {translate("streetAccess")}
          </label>
          <div className="w-100 flex-column">
            {/* First row - Street Access */}
            <Grid container direction="row" alignItems="center">
              <Grid item sm md={3} lg={3}>
                <Checkbox
                  value={translate("wide")}
                  checked={streetNames?.includes(translate("wide"))}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  label={translate("wide")}
                  labelPlacement={Placement.end}
                  disabled={viewMode}
                />
              </Grid>
              <Grid item sm md={2} lg={2}>
                <Checkbox
                  value={translate("narrow")}
                  checked={streetNames?.includes(translate("narrow"))}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  label={translate("narrow")}
                  labelPlacement={Placement.end}
                  disabled={viewMode}
                />
              </Grid>
              <Grid item sm md={2} lg={2}>
                <Checkbox
                  value={translate("step")}
                  checked={streetNames?.includes(translate("step"))}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  label={translate("step")}
                  labelPlacement={Placement.end}
                  disabled={viewMode}
                />
              </Grid>
              <Grid item sm md={2} lg={2}>
                <Checkbox
                  value={translate("flat")}
                  checked={streetNames?.includes(translate("flat"))}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  label={translate("flat")}
                  labelPlacement={Placement.end}
                  disabled={viewMode}
                />
              </Grid>
              <Grid item sm md={3} lg={3}>
                <Checkbox
                  value={translate("normal")}
                  checked={streetNames?.includes(translate("normal"))}
                  label={translate("normal")}
                  labelPlacement={Placement.end}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  disabled={viewMode}
                />
              </Grid>
            </Grid>

            {/* Second row - Street Access */}
            <Grid container direction="row" alignItems="center">
              <Grid item sm md={3} lg={3}>
                <Checkbox
                  value={translate("permitRequired")}
                  checked={streetNames?.includes(translate("permitRequired"))}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  label={translate("permitRequired")}
                  labelPlacement={Placement.end}
                  disabled={viewMode}
                />
              </Grid>
              <Grid item sm md={2} lg={2}>
                <Checkbox
                  value={translate("oneWay")}
                  checked={streetNames?.includes(translate("oneWay"))}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  label={translate("oneWay")}
                  labelPlacement={Placement.end}
                  disabled={viewMode}
                />
              </Grid>
              <Grid item sm md={2} lg={2}>
                <Checkbox
                  value={translate("culDeSac")}
                  checked={streetNames?.includes(translate("culDeSac"))}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  label={translate("culDeSac")}
                  labelPlacement={Placement.end}
                  disabled={viewMode}
                />
              </Grid>
              <Grid item sm md={2} lg={2}>
                <Checkbox
                  value={translate("roundabout")}
                  checked={streetNames?.includes(translate("roundabout"))}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  label={translate("roundabout")}
                  labelPlacement={Placement.end}
                  disabled={viewMode}
                />
              </Grid>
              <Grid item sm md={3} lg={3}>
                <Checkbox
                  value={translate("speedHumps")}
                  checked={streetNames?.includes(translate("speedHumps"))}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  label={translate("speedHumps")}
                  labelPlacement={Placement.end}
                  disabled={viewMode}
                />
              </Grid>
            </Grid>

            {/* Third row - Street Access */}
            <Grid container direction="row" alignItems="center">
              <Grid item sm md={3} lg={3}>
                <Checkbox
                  value={translate("noThroughRoad")}
                  checked={streetNames?.includes(translate("noThroughRoad"))}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeStreetAccess(event.target.value)
                  }
                  label={translate("noThroughRoad")}
                  labelPlacement={Placement.end}
                  disabled={viewMode}
                />
              </Grid>
            </Grid>
          </div>
        </div>

        {/* Parking and Carry distance */}
        <GenericForm
          classes="mt-1 mb-3"
          leftInputs={
            <div className="d-flex align-items-end">
              <Select
                label={translate("parking")}
                labelStyle={{ color: "#465869" }}
                name="parkingType"
                disabled={viewMode}
                value={getValue(accessInfo?.parking)}
                onChange={(event: SelectChangeEvent<any>) =>
                  handleChangeAccess("parking", event.target.value)
                }
                displayEmpty
                placeholder={translate("select")}
              >
                {parkingOptions.map((option: { label: string; value: string }) => (
                  <MenuItem value={option.value} key={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <div className="d-flex align-items-center flex-row ml-3">
                <Label className="mx-2">{translate("carryM")}</Label>
                <Select
                  name="carry"
                  value={getValue(accessInfo?.carryDistance)}
                  disabled={viewMode}
                  onChange={(event: SelectChangeEvent<any>) =>
                    handleChangeAccess("carryDistance", event.target.value)
                  }
                  displayEmpty
                  placeholder={translate("select")}
                >
                  {carryOptions.map((option: { label: string; value: string }) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          }
        />

        {/* Trees/Powerline  */}
        <GenericForm
          label={translate("treesPowerline")}
          classes="mt-3"
          leftInputs={
            <FormGroup row aria-label="powerline" className={classes.topReverseSpace}>
              <Checkbox
                className={classes.rightSpace}
                value="overhanging"
                disabled={viewMode}
                checked={!!accessInfo?.treeOrPowerlineOverhanging}
                onChange={(event, checked) =>
                  accessInfo &&
                  handleUpdateAccessInfoAndData({
                    ...accessInfo,
                    treeOrPowerlineOverhanging: checked,
                  })
                }
                label={translate("overhanging")}
              />
              <Checkbox
                value="low"
                disabled={viewMode}
                checked={!!accessInfo?.treeOrPowerlineLow}
                onChange={(event, checked) =>
                  accessInfo &&
                  handleUpdateAccessInfoAndData({
                    ...accessInfo,
                    treeOrPowerlineLow: checked,
                  })
                }
                label={translate("low")}
              />
            </FormGroup>
          }
          rightInputs={
            <div className="d-flex align-items-center ml-3">
              <Label className="mr-2">{translate("action")}</Label>
              <Select
                name="actionType"
                disabled={viewMode}
                value={getValue(accessInfo?.treeOrPowerlineAction)}
                onChange={(event: SelectChangeEvent<any>) =>
                  handleChangeAccess("treeOrPowerlineAction", event.target.value)
                }
                displayEmpty
                placeholder={translate("select")}
              >
                {actionOptions.map((option: { label: string; value: string }) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          }
        />

        {/* Vehicle size */}
        <GenericForm
          label={translate("vehicleSize")}
          classes="mt-1"
          leftInputs={
            <RadioGroup
              row
              aria-label="vehicleSize"
              name="row-radio-buttons-group"
              disabled={viewMode}
              value={getValue(accessInfo?.vehicle)}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeAccess("vehicle", event.target.value)
              }
              options={[
                { label: translate("small"), value: "small" },
                { label: translate("rigid"), value: "rigid" },
                { label: translate("semi"), value: "semi" },
              ]}
            />
          }
          rightInputs={
            <>
              <Label className="mx-3">{translate("isFerryRequired")}</Label>
              <RadioGroup
                row
                aria-label="ferryRequired"
                style={{ marginTop: "6px" }}
                name="row-radio-buttons-group"
                disabled={viewMode}
                value={getValue(accessInfo?.ferryRequired, true)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeAccess("ferryRequired", event.target.value === "true")
                }
                options={[
                  { label: translate("yes"), value: "true" },
                  { label: translate("no"), value: "false" },
                ]}
              />
            </>
          }
        />

        {/* Comment  */}
        <GenericForm
          classes="mt-1 mb-1"
          label={translate("comments")}
          leftInputs={
            <Input
              fullWidth
              multiline
              minRows={3}
              maxRows={5}
              disabled={viewMode}
              placeholder={translate("inputYourComments")}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                setCommentsField(event.target.value)
              }
              value={commentsField}
              onBlur={() => handleChangeAccess("comments", commentsField)}
            />
          }
        />

        {/* Access details */}
        <CheckBoxAccess
          label={translate("accessDetailsDescribedAboveAreAccurateToTheBestOfMyKnowledge")}
          value={accessInfo?.accessDetailsDescribed}
          checked={!!accessInfo?.accessDetailsDescribed}
          disabled={Boolean(accessInfo?.id && accessInfo?.accessDetailsDescribed) || viewMode}
          onChange={(event, checked) => handleChangeAccess("accessDetailsDescribed", checked)}
          error={fieldHasError(described)}
          hasError={fieldHasError(described)}
        />

        <div className="mt-2">
          <div className="d-flex align-items-center mb-2">
            <p className={`mb-0 ${classes.instructionListLabel}`}>
              {translate("specialInstructions")}
            </p>
            <Button
              data-cy="buttonAddInstruction"
              startIcon={<Add />}
              disabled={!addInstructionEnabled}
              onClick={() => setIsAddInstruction(true)}
              customColor={Color.SECONDARY}
              className={classes.buttonAddSpecialInstruction}
            >
              {translate("accessInstructions")}
            </Button>
          </div>
          {accessInfo &&
            accessInfo?.instructions?.filter(
              (instruction) => instruction.when.toLowerCase() === "origin"
            ).length > 0 && (
              <InstructionsList
                label={translate("pickUp")}
                icon={<Archive />}
                data={accessInfo?.instructions?.filter(
                  (instruction) => instruction.when.toLowerCase() === "origin"
                )}
                updateList={(data: Instruction[]) => {
                  const deliveryInstruction = accessInfo.instructions.filter(
                    (instruction) => instruction.when.toLowerCase() === "destination"
                  );

                  handleUpdateAccessInfoAndData({
                    ...accessInfo,
                    instructions: [...data, ...deliveryInstruction],
                  });
                }}
              />
            )}
          {accessInfo &&
            accessInfo?.instructions?.filter(
              (instruction) => instruction.when.toLowerCase() === "destination"
            ).length > 0 && (
              <InstructionsList
                label={translate("delivery")}
                icon={<LocalShipping />}
                data={accessInfo?.instructions?.filter(
                  (instruction) => instruction.when.toLowerCase() === "destination"
                )}
                updateList={(data: Instruction[]) => {
                  const pickupInstructions = accessInfo.instructions.filter(
                    (instruction) => instruction.when.toLowerCase() === "origin"
                  );

                  handleUpdateAccessInfoAndData({
                    ...accessInfo,
                    instructions: [...data, ...pickupInstructions],
                  });
                }}
              />
            )}
        </div>
      </section>

      <AddSpecialInstructionModal
        open={isAddInstruction}
        closeModal={() => setIsAddInstruction(false)}
        handleChange={(type: string, value: string) => {
          const obj: Instruction = {
            when: type === "pickup" ? "Origin" : "Destination",
            remarks: value,
            createdBy: currentUser,
            createdById: currentUser?.id,
          };

          if (accessInfo) {
            if (accessInfo?.instructions && accessInfo?.instructions?.length > 0)
              handleUpdateAccessInfoAndData({
                ...accessInfo,
                // eslint-disable-next-line no-unsafe-optional-chaining
                instructions: [...accessInfo?.instructions, obj],
              });
            else
              handleUpdateAccessInfoAndData({
                ...accessInfo,
                instructions: [obj],
              });
            setAddInstructionEnabled(false);
          }
        }}
      />
    </>
  );
};

export default AccessInfoForm;
